import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

/**
 * Screening questions the Apply Tool collected on a job-board form, plus the
 * answers the user typed in this app.
 *
 * Why this is separate from match-questions.ts: those questions are LoopCV's
 * own, and submitting them goes upstream (LoopCV resumes the apply). These
 * belong to the extension's run - the tool re-reads the answers on its next
 * run and types them into the form itself, so nothing here is sent upstream.
 */

export interface ExtensionQuestionOption {
  name: string
  value: string
}

export interface ExtensionQuestion {
  id: string
  label: string
  type: string
  options?: ExtensionQuestionOption[]
}

export interface ExtensionQuestionSet {
  matchId: string
  jobTitle: string
  company: string
  questions: ExtensionQuestion[]
  /** label to answer; only the labels the user already answered. */
  answers: Record<string, string>
  updatedAt: string
}

export const extensionQuestionsKey = ['extension-questions'] as const

/** Pending sets only - the backend hides the ones already answered. */
export function extensionQuestionsQuery() {
  return queryOptions({
    queryKey: extensionQuestionsKey,
    queryFn: async () => {
      const res = await api.get<{ items: ExtensionQuestionSet[] }>('/questions')
      return res.items
    },
  })
}

export function useSaveExtensionAnswersMutation(matchId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (answers: Record<string, string>) =>
      api.put<ExtensionQuestionSet>(`/questions/${matchId}`, { answers }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: extensionQuestionsKey }),
  })
}
